import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AlertController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { compact } from 'lodash-es';
import { Observable } from 'rxjs';

import {
  PrimarySubscriptionAccessActionEnum,
  PrimarySubscriptionPlanAccessAliasEnum
} from '../../../../../../enums/monetization';
import * as fromWalkingFarmActions from '../../../../../../store/actions/walking-farm.actions';
import * as fromLayoutSelectors from '../../../../../../store/selectors/layout.selectors';
import * as fromOrganizationSelectors from '../../../../../../store/selectors/organization.selectors';
import * as fromWalkingFarmSelectors from '../../../../../../store/selectors/walking-farm.selectors';
import { SavedFarmListItem } from '../../../../../../typings/walking-farm';
import { MonetizationService } from '../../../../../services/monetization.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-saved',
  standalone: false,
  templateUrl: './saved-farms.component.html'
})
export class SavedFarmsComponent {
  private activatedRoute = inject(ActivatedRoute);
  private alertController = inject(AlertController);
  private monetizationService = inject(MonetizationService);
  private router = inject(Router);
  private store = inject(Store);

  savedFarms$: Observable<SavedFarmListItem[]>;
  turnoverRateEnabled$: Observable<boolean>;
  selectFarmAlertsEnabled$: Observable<boolean>;
  useMobileLayout$: Observable<boolean>;

  defaultBackHref: string | undefined;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];

    this.savedFarms$ = this.store.pipe(select(fromWalkingFarmSelectors.selectSavedFarms));
    this.turnoverRateEnabled$ = this.store.pipe(select(fromOrganizationSelectors.selectTurnoverRateEnabled));
    this.selectFarmAlertsEnabled$ = this.store.pipe(select(fromOrganizationSelectors.selectFarmAlertsEnabled));
    this.useMobileLayout$ = this.store.pipe(select(fromLayoutSelectors.selectUseMobileLayout));
  }

  savedFarmsListTrackBy(_index: number, savedFarm: SavedFarmListItem) {
    return savedFarm.id;
  }

  async handleDelete(event: MouseEvent, savedFarm: SavedFarmListItem) {
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
    if (hasAccess) {
      const alert = await this.alertController.create({
        buttons: [
          {
            role: 'cancel',
            text: 'Cancel'
          },
          {
            role: 'destructive',
            text: 'Delete'
          }
        ],
        header: 'Delete Farm',
        message: `Are you sure? You are about to delete the farm "${savedFarm.name}". This action cannot be undone.`
      });

      await alert.present();

      const { role } = await alert.onDidDismiss();
      if (role === 'destructive') {
        this.store.dispatch(fromWalkingFarmActions.deleteSavedFarm({ payload: { id: savedFarm.id } }));
      }
    }
  }

  async openSavedFarmAlerts(event: MouseEvent, id: string) {
    event.stopPropagation();
    const hasAccess = await this.monetizationService.accessFeature(
      [PrimarySubscriptionPlanAccessAliasEnum.A, PrimarySubscriptionPlanAccessAliasEnum.C],
      { accessAction: PrimarySubscriptionAccessActionEnum.ViewFarm }
    );
    if (hasAccess) {
      await this.router.navigate([this.defaultBackHref ?? '/', 'walking-farm', id, 'alerts']);
    }
  }

  async openSavedFarm(id: string) {
    const hasAccess = await this.monetizationService.accessFeature(
      [PrimarySubscriptionPlanAccessAliasEnum.A, PrimarySubscriptionPlanAccessAliasEnum.C],
      { accessAction: PrimarySubscriptionAccessActionEnum.ViewFarm }
    );
    if (hasAccess) {
      this.store.dispatch(
        fromWalkingFarmActions.updatePhoneAndEmailSelectedPropertyIds({
          payload: { farmId: id, phoneAndEmailSelectedPropertyIds: [] }
        })
      );
      await this.router.navigate(compact([this.defaultBackHref ?? '/', 'walking-farm', id, 'view']));
    }
  }

  asSavedFarm(savedFarm: SavedFarmListItem) {
    return savedFarm;
  }
}
